import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, CheckCircle, ArrowRight, Play } from 'lucide-react';
import PageHero from '../components/PageHero';
import { fadeUp, stagger, viewportOpts } from '../hooks/useScrollAnimation';
import { activities, specialEvents } from '../data/activities';
import { events } from '../data/events';
import * as Icons from 'lucide-react';

const Wave = ({ fill, bg = "transparent" }) => (
  <div className="wave-divider" style={{ background: bg }}>
    <svg viewBox="0 0 1440 70" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ height: 50 }}>
      <path d="M0,35 C200,70 400,0 720,35 C1040,70 1280,5 1440,35 L1440,70 L0,70 Z" fill={fill} />
    </svg>
  </div>
);

// Dynamic Lucide Icon loader component
const DynamicIcon = ({ name, fallback = "Sparkles", className, size }) => {
  const IconComponent = Icons[name] || Icons[fallback];
  return <IconComponent className={className} size={size} />;
};

export default function Activities() {
  return (
    <div>
      <PageHero 
        title="Learning Beyond the Classroom" 
        subtitle="Our co-curricular activities help children express themselves, develop confidence, stay active, discover interests, and enjoy meaningful social experiences." 
        breadcrumb="Activities" 
      />

      {/* Co-Curricular Activities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Co-Curriculars</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Co-Curricular Activities</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">Diverse activities that support fine motor, balance, creativity, and logic outside core academics.</p>
          </motion.div>

          <motion.div 
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOpts}
            variants={stagger}
          >
            {activities.map((act, i) => (
              <motion.div 
                key={i} 
                variants={fadeUp} 
                className="cartoon-card bg-white rounded-3xl p-8 border border-gray-100 shadow-sm flex gap-4 items-start"
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: act.color }}>
                  <DynamicIcon name={act.iconName} fallback={act.iconNameFallback || "Sparkles"} size={26} className="text-white" style={{ color: act.iconColor }} />
                </div>
                <div>
                  <h3 className="font-nunito font-bold text-xl text-[#1A1A1A] mb-2">{act.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{act.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Wave fill="#F8FAFC" bg="white" />

      {/* Special Events */}
      <section className="py-20 bg-[#F8FAFC] polka-dots">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Celebrations</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Special Events</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">At Happykidz Preschool, every celebration becomes an opportunity for participation, cultural awareness, and confidence building.</p>
          </motion.div>

          <motion.div 
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOpts}
            variants={stagger}
          >
            {specialEvents.map((evt, i) => (
              <motion.div 
                key={i} 
                variants={fadeUp} 
                className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 flex items-start gap-3"
              >
                <CheckCircle className="text-[#9DC41E] flex-shrink-0 mt-1" size={18} />
                <div>
                  <h3 className="font-nunito font-bold text-lg text-[#1A1A1A] mb-1">{evt.name}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{evt.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Wave fill="white" bg="#F8FAFC" />

      {/* Recent & Upcoming Events Feed */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">School Calendar</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Upcoming &amp; Recent Events</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">Stay updated with our latest workshops, orientations, camps, and festival meets.</p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOpts}
            variants={stagger}
          >
            {events.map((item, i) => (
              <motion.div 
                key={i} 
                variants={fadeUp} 
                className="bg-white rounded-3rem overflow-hidden border border-gray-100 shadow-lg flex flex-col md:flex-row"
              >
                {/* Visual side */}
                <div className="md:w-1/3 bg-[#FDE8E9] relative flex items-center justify-center p-6 min-h-[150px]">
                  <Calendar size={48} className="text-[#C8161D]" />
                  <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold ${
                    item.status === 'Upcoming' ? 'bg-[#9DC41E] text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {item.status}
                  </span>
                </div>
                
                {/* Content side */}
                <div className="p-8 md:w-2/3 flex flex-col justify-between">
                  <div>
                    <span className="text-[#C8161D] text-xs font-bold tracking-wider uppercase block mb-1">{item.date}</span>
                    <h3 className="font-nunito font-extrabold text-2xl text-[#1A1A1A] mb-3">{item.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4">{item.desc}</p>
                  </div>
                  <div>
                    {item.status === 'Upcoming' ? (
                      <a href="#register" className="inline-flex items-center gap-1.5 text-[#C8161D] font-bold text-sm hover:underline">
                        {item.ctaText} <ArrowRight size={14} />
                      </a>
                    ) : (
                      <span className="text-gray-400 text-sm font-semibold">{item.status}</span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
